import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Wrench, Check, X, Loader2, CreditCard, ArrowDownRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { isNativeStripeAvailable } from "@/lib/stripeNative";

const eur = (n) => `${Number(n || 0).toFixed(2)}€`;
const MY_TURN = {
  client: ["driver_proposed", "driver_countered", "driver_refused"],
  driver: ["passenger_countered"],
};

// Négociation des frais d'usure (trajets ≥ 50 km) — passager & conducteur.
export default function WearNegotiationPopup() {
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState({});
  const load = useCallback(async () => {
    try { const r = await api.get("/price/wear/pending"); setItems(r.data?.items || []); } catch (_e) { /* silencieux */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(t);
  }, [load]);

  const it = items.find((x) => {
    const w = x.wear;
    const sig = `${x.id}:${w.status}:${w.amount_pp}:${w.passenger_offer_pp}`;
    if (hidden[sig]) return false;
    return MY_TURN[x.role].includes(w.status) || (x.role === "client" && w.status === "agreed" && w.payment === "card" && !w.paid);
  });
  if (!it) return null;
  const sig = `${it.id}:${it.wear.status}:${it.wear.amount_pp}:${it.wear.passenger_offer_pp}`;
  return createPortal(
    <WearCard key={sig} it={it} onDone={load} onLater={() => setHidden((h) => ({ ...h, [sig]: true }))} />,
    document.body,
  );
}

function WearCard({ it, onDone, onLater }) {
  const w = it.wear;
  const lp = it.long_pricing || {};
  const isClient = it.role === "client";
  const [counter, setCounter] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onDone(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const respond = (action, amount_pp) => run(async () => {
    await api.post(`/price/wear/${it.kind}/${it.id}/respond`, { action, amount_pp });
    toast.success(action === "accept" ? "Frais d'usure validés ✅" : action === "refuse" ? "Offre refusée" : "Proposition envoyée 💬");
  });
  const pay = () => run(async () => {
    const r = await api.post(`/price/wear/${it.kind}/${it.id}/checkout`, { origin_url: window.location.origin });
    if (isNativeStripeAvailable()) {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: r.data.checkout_url, presentationStyle: "popover" });
    } else {
      window.location.href = r.data.checkout_url;
    }
  });
  const counterNum = Number(String(counter).replace(",", "."));
  const offer = w.passenger_offer_pp;
  const awaitingPay = w.status === "agreed";
  const title = awaitingPay ? "Règle les frais d'usure" : isClient
    ? (w.status === "driver_refused" ? "Le covoitureur a refusé ton offre" : "Frais d'usure du véhicule")
    : "Le passager propose un autre montant";

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm p-0 sm:p-3" data-testid="wear-popup">
      <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-[#FFF8F1] p-5 street-border street-shadow animate-fade-up">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent street-border-thin rotate-[-4deg]"><Wrench className="h-5 w-5" strokeWidth={2.5} /></div>
          <div className="min-w-0">
            <div className="font-[Outfit] text-[17px] font-black leading-tight">{title}</div>
            <div className="truncate text-[11px] font-semibold text-foreground/60">{it.from_address} → {it.to_address}</div>
          </div>
        </div>

        <div className="mt-4 space-y-1.5 rounded-2xl bg-white p-3 text-[13px] street-border-thin" data-testid="wear-breakdown">
          <Row label="Carburant + péages (non négociables)" value={eur((lp.fuel_eur + lp.toll_eur) / (lp.divisor || 1))} hint="par passager" />
          <Row label="Usure demandée par le covoitureur" value={eur(w.amount_pp)} hint="par passager" strong testid="wear-amount" />
          {offer != null && <Row label="Offre du passager" value={eur(offer)} hint="par passager" testid="wear-offer" />}
          <div className="pt-1 text-[10px] font-semibold text-foreground/50">
            Plafond légal d&apos;usure : {eur(w.cap_pp)} / passager · {w.seats} passager(s) · paiement séparé de la course
          </div>
        </div>

        {awaitingPay ? (
          <button type="button" disabled={busy} onClick={pay} data-testid="wear-pay" className="street-press mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black text-primary-foreground street-border street-shadow disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Payer {eur(w.amount_pp * w.seats)}
          </button>
        ) : (
          <>
            <button type="button" disabled={busy} onClick={() => respond("accept")} data-testid="wear-accept"
              className="street-press mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black text-primary-foreground street-border street-shadow disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={3} />}
              {isClient ? `Accepter ${eur(w.amount_pp)} / passager` : `Accepter ${eur(offer)} / passager`}
            </button>
            {(isClient ? w.status !== "driver_refused" : true) && (
              <div className="mt-2 flex gap-2">
                <input inputMode="decimal" value={counter} onChange={(e) => setCounter(e.target.value)}
                  placeholder={isClient ? `Moins de ${eur(w.amount_pp)}` : `Entre ${eur(offer)} et ${eur(Math.min(w.cap_pp, w.amount_pp))}`}
                  data-testid="wear-counter-input" style={{ fontSize: "16px" }}
                  className="h-12 min-w-0 flex-1 rounded-xl bg-white px-3 font-[Outfit] font-black street-border-thin focus:outline-none" />
                <button type="button" disabled={busy || !(counterNum >= 0) || counter === ""} onClick={() => respond("counter", counterNum)} data-testid="wear-counter"
                  className="street-press inline-flex items-center gap-1 rounded-xl bg-white px-3 text-[12px] font-black street-border-thin disabled:opacity-50">
                  <ArrowDownRight className="h-4 w-4" /> {isClient ? "Proposer" : "Contre-proposer"}
                </button>
              </div>
            )}
            {!isClient && (
              <button type="button" disabled={busy} onClick={() => respond("refuse")} data-testid="wear-refuse"
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl bg-white py-2.5 text-[12px] font-black street-border-thin">
                <X className="h-4 w-4" /> Refuser l&apos;offre
              </button>
            )}
            {isClient && w.status === "driver_refused" && (
              <p className="mt-2 text-[11px] font-semibold text-foreground/60">Tu peux accepter le montant du covoitureur ou annuler ta course.</p>
            )}
          </>
        )}
        <button type="button" onClick={onLater} data-testid="wear-later" className="mt-3 w-full text-center text-[11px] font-black uppercase tracking-widest text-foreground/50">Plus tard</button>
      </div>
    </div>
  );
}

function Row({ label, value, hint, strong, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-2" data-testid={testid}>
      <span className="text-foreground/70">{label}</span>
      <span className={`text-right tabular-nums ${strong ? "font-[Outfit] text-[16px] font-black text-primary" : "font-black"}`}>
        {value}<span className="ml-1 text-[9px] font-bold text-foreground/45">{hint}</span>
      </span>
    </div>
  );
}
